import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { geoguesserService } from '../../../services/api';
import FloorImageDropZone from './FloorImageDropZone';
import SortableFloorCard from './SortableFloorCard';

/**
 * Self-contained floor editor for a single GeoGuesser map.
 *
 * Owns: the `floors` array, per-floor edit buffers, auto-save state,
 * drag-and-drop ordering, and the "new floor" composer.
 *
 * Communicates with the outside world through a small prop surface:
 *
 *   - `mapId`        – required; all REST calls scope to this map.
 *   - `initialFloors`– optional server-fetched list the parent already
 *                      has in hand (e.g. from a bigger /map endpoint).
 *                      When provided we skip the initial GET.
 *   - `onFloorsChange(floors)` – fired any time the authoritative floor
 *                      list changes. Sibling components (the level
 *                      assigner especially) use this to keep their
 *                      target-floor dropdown in sync.
 *   - `onError(msg)` – surface errors to the parent's banner. The
 *                      component does not render its own error UI.
 *   - `onSuccess(msg)` – transient success toast hook; optional.
 *   - `disabled`     – when true, all inputs are hidden from keyboard
 *                      and auto-save is paused (useful for read-only
 *                      contexts).
 *
 * Every edit auto-saves: names are debounced by 600ms, images + the
 * default toggle fire immediately. The new-floor card auto-creates
 * once both a name and an image are present (1s idle debounce).
 */
const emptyFloorForm = () => ({
  name: '',
  is_default: false,
  description: '',
  file: null,
  filePreview: null,
});

const errorDetail = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'object' && data) {
    const entries = Object.entries(data);
    if (entries.length > 0) {
      return entries
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
        .join('; ');
    }
  }
  return data?.detail || fallback;
};

const FloorManager = ({
  mapId,
  initialFloors,
  onFloorsChange,
  onError,
  onSuccess,
  disabled = false,
}) => {
  const [floors, setFloors] = useState(initialFloors || []);
  const [floorBuffers, setFloorBuffers] = useState({});
  const [floorSaveState, setFloorSaveState] = useState({});
  const [newFloor, setNewFloor] = useState(emptyFloorForm);
  const [newFloorSaveState, setNewFloorSaveState] = useState(null);
  const [loading, setLoading] = useState(!initialFloors);

  // Refs used by async / timer-bound work that needs the freshest
  // state without re-running effects.
  const floorsRef = useRef([]);
  const floorBuffersRef = useRef({});
  const floorNameTimersRef = useRef({});
  const newFloorCreatingRef = useRef(false);
  const newFloorRef = useRef(newFloor);
  const floorsCountRef = useRef(floors.length);

  useEffect(() => {
    floorsRef.current = floors;
    floorsCountRef.current = floors.length;
    if (typeof onFloorsChange === 'function') onFloorsChange(floors);
  }, [floors, onFloorsChange]);
  useEffect(() => {
    floorBuffersRef.current = floorBuffers;
  }, [floorBuffers]);
  useEffect(() => {
    newFloorRef.current = newFloor;
  }, [newFloor]);

  const reportError = useCallback(
    (msg) => {
      if (typeof onError === 'function') onError(msg);
    },
    [onError],
  );
  const reportSuccess = useCallback(
    (msg) => {
      if (typeof onSuccess === 'function') onSuccess(msg);
    },
    [onSuccess],
  );

  // Seed edit buffers from an authoritative floors list.
  const seedBuffers = useCallback((list) => {
    const buffers = {};
    for (const f of list) {
      buffers[f.id] = {
        name: f.name,
        is_default: !!f.is_default,
        description: f.description || '',
        file: null,
        filePreview: null,
      };
    }
    setFloorBuffers(buffers);
  }, []);

  // Initial load — only needed when the parent didn't pass in
  // `initialFloors`. After this, state is driven by PATCH responses
  // (so we never wipe in-progress buffers with a full reload).
  useEffect(() => {
    if (!mapId) return;
    if (initialFloors) {
      setFloors(initialFloors);
      seedBuffers(initialFloors);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await geoguesserService.getFloors(mapId);
        if (cancelled) return;
        const list = (
          Array.isArray(res.data) ? res.data : res.data?.results || []
        ).sort((a, b) => a.order - b.order);
        setFloors(list);
        seedBuffers(list);
      } catch (err) {
        if (!cancelled) reportError(errorDetail(err, 'Failed to load floors.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mapId, initialFloors, seedBuffers, reportError]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // --- Auto-save plumbing -------------------------------------------------

  const setFloorSaveStatus = useCallback((floorId, next) => {
    setFloorSaveState((prev) => {
      if (next == null) {
        if (!(floorId in prev)) return prev;
        const out = { ...prev };
        delete out[floorId];
        return out;
      }
      return { ...prev, [floorId]: next };
    });
  }, []);

  const autoSaveFloor = useCallback(
    async (floorId, fields, file = null) => {
      if (disabled) return;
      const payload = new FormData();
      for (const [k, v] of Object.entries(fields || {})) {
        payload.append(k, String(v));
      }
      if (file) payload.append('floor_image', file);

      setFloorSaveStatus(floorId, 'saving');
      try {
        const res = await geoguesserService.updateFloor(floorId, payload);
        const updated = res?.data || {};
        setFloors((prev) =>
          prev.map((f) => (f.id === floorId ? { ...f, ...updated } : f)),
        );
        if (file) {
          setFloorBuffers((prev) => ({
            ...prev,
            [floorId]: { ...prev[floorId], file: null, filePreview: null },
          }));
        }
        setFloorSaveStatus(floorId, 'saved');
        window.setTimeout(() => {
          setFloorSaveState((prev) => {
            if (prev[floorId] !== 'saved') return prev;
            const out = { ...prev };
            delete out[floorId];
            return out;
          });
        }, 1200);
      } catch (err) {
        setFloorSaveStatus(floorId, 'error');
        reportError(errorDetail(err, 'Failed to auto-save floor.'));
      }
    },
    [disabled, reportError, setFloorSaveStatus],
  );

  // Flush any outstanding debounced name saves on unmount so closing
  // the modal / navigating away doesn't lose the last keystroke.
  useEffect(() => {
    return () => {
      for (const [floorId, timerId] of Object.entries(
        floorNameTimersRef.current,
      )) {
        window.clearTimeout(timerId);
        const buf = floorBuffersRef.current[floorId];
        const trimmed = (buf?.name || '').trim();
        if (trimmed) {
          const payload = new FormData();
          payload.append('name', trimmed);
          geoguesserService.updateFloor(floorId, payload).catch(() => {
            // eslint-disable-next-line no-console
            console.warn('Flush floor name on unmount failed', floorId);
          });
        }
      }
      floorNameTimersRef.current = {};
    };
  }, []);

  // --- Floor handlers -----------------------------------------------------

  const handleFloorNameChange = useCallback(
    (floorId, name) => {
      setFloorBuffers((prev) => ({
        ...prev,
        [floorId]: { ...prev[floorId], name },
      }));
      if (floorNameTimersRef.current[floorId]) {
        window.clearTimeout(floorNameTimersRef.current[floorId]);
      }
      floorNameTimersRef.current[floorId] = window.setTimeout(() => {
        delete floorNameTimersRef.current[floorId];
        const trimmed = (name || '').trim();
        if (!trimmed) return;
        autoSaveFloor(floorId, { name: trimmed });
      }, 600);
    },
    [autoSaveFloor],
  );

  const handleFloorNameBlur = useCallback(
    (floorId) => {
      const timer = floorNameTimersRef.current[floorId];
      if (!timer) return;
      window.clearTimeout(timer);
      delete floorNameTimersRef.current[floorId];
      const buf = floorBuffersRef.current[floorId];
      const trimmed = (buf?.name || '').trim();
      if (!trimmed) return;
      autoSaveFloor(floorId, { name: trimmed });
    },
    [autoSaveFloor],
  );

  const handleFloorSetDefault = useCallback(
    (floorId) => {
      setFloorBuffers((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = {
            ...next[key],
            is_default: String(key) === String(floorId),
          };
        }
        return next;
      });
      for (const f of floorsRef.current) {
        if (f.id !== floorId && f.is_default) {
          autoSaveFloor(f.id, { is_default: 'false' });
        }
      }
      autoSaveFloor(floorId, { is_default: 'true' });
    },
    [autoSaveFloor],
  );

  const handleFloorImageChange = useCallback(
    (id, file) => {
      if (!file) {
        setFloorBuffers((prev) => ({
          ...prev,
          [id]: { ...prev[id], file: null, filePreview: null },
        }));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFloorBuffers((prev) => ({
          ...prev,
          [id]: { ...prev[id], file, filePreview: reader.result },
        }));
        autoSaveFloor(id, {}, file);
      };
      reader.readAsDataURL(file);
    },
    [autoSaveFloor],
  );

  const handleDeleteFloor = useCallback(
    async (floor) => {
      const prompt = `Delete floor "${floor.name}"?\n\nAny levels still on this floor will be deleted with it. Reassign them first if you want to keep them.`;
      if (!window.confirm(prompt)) return;
      try {
        await geoguesserService.deleteFloor(floor.id);
        setFloors((prev) => prev.filter((f) => f.id !== floor.id));
        setFloorBuffers((prev) => {
          const next = { ...prev };
          delete next[floor.id];
          return next;
        });
        reportSuccess(`Deleted "${floor.name}".`);
      } catch (err) {
        reportError(errorDetail(err, 'Failed to delete floor.'));
      }
    },
    [reportError, reportSuccess],
  );

  const handleFloorsReorder = useCallback(
    async (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const list = floorsRef.current;
      const oldIndex = list.findIndex(
        (f) => String(f.id) === String(active.id),
      );
      const newIndex = list.findIndex((f) => String(f.id) === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;

      const reordered = arrayMove(list, oldIndex, newIndex).map((f, idx) => ({
        ...f,
        order: idx,
      }));
      setFloors(reordered);

      const updates = reordered
        .map((f, idx) => {
          const original = list.find((o) => o.id === f.id);
          if (!original || original.order === idx) return null;
          const form = new FormData();
          form.append('order', String(idx));
          return geoguesserService.updateFloor(f.id, form);
        })
        .filter(Boolean);

      try {
        await Promise.all(updates);
        reportSuccess('Floor order updated.');
      } catch (err) {
        reportError(errorDetail(err, 'Failed to save new floor order.'));
        // On failure the optimistic list may be stale but we don't
        // reload — preserving in-progress edits is more important than
        // a rare ordering mismatch. The next successful save (or the
        // next page load) will reconcile.
      }
    },
    [reportError, reportSuccess],
  );

  // --- New-floor card -----------------------------------------------------

  const handleNewFloorImageChange = (file) => {
    if (!file) {
      setNewFloor((f) => ({ ...f, file: null, filePreview: null }));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () =>
      setNewFloor((f) => ({ ...f, file, filePreview: reader.result }));
    reader.readAsDataURL(file);
  };

  const autoCreateNewFloor = useCallback(async () => {
    if (disabled) return;
    if (newFloorCreatingRef.current) return;
    const cur = newFloorRef.current;
    const name = (cur.name || '').trim();
    if (!name || !cur.file) return;

    newFloorCreatingRef.current = true;
    setNewFloorSaveState('saving');
    const payload = new FormData();
    payload.append('map', mapId);
    payload.append('name', name);
    payload.append('order', String(floorsCountRef.current));
    payload.append('is_default', cur.is_default ? 'true' : 'false');
    payload.append('description', cur.description || '');
    payload.append('floor_image', cur.file);
    try {
      const res = await geoguesserService.createFloor(payload);
      const created = res?.data || {};
      setFloors((prev) => [...prev, created]);
      setFloorBuffers((prev) => ({
        ...prev,
        [created.id]: {
          name: created.name,
          is_default: !!created.is_default,
          description: created.description || '',
          file: null,
          filePreview: null,
        },
      }));
      setNewFloor(emptyFloorForm());
      setNewFloorSaveState('saved');
      reportSuccess(`Added "${name}".`);
      window.setTimeout(() => {
        setNewFloorSaveState((prev) => (prev === 'saved' ? null : prev));
      }, 1200);
    } catch (err) {
      reportError(errorDetail(err, 'Failed to create floor.'));
      setNewFloorSaveState('error');
    } finally {
      newFloorCreatingRef.current = false;
    }
  }, [disabled, mapId, reportError, reportSuccess]);

  useEffect(() => {
    if (disabled) return undefined;
    const name = (newFloor.name || '').trim();
    if (!name || !newFloor.file) return undefined;
    if (newFloorCreatingRef.current) return undefined;
    const timer = window.setTimeout(() => {
      autoCreateNewFloor();
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [
    disabled,
    newFloor.name,
    newFloor.file,
    newFloor.is_default,
    autoCreateNewFloor,
  ]);

  // --- Render -------------------------------------------------------------

  if (loading) return <p className="wizard-help">Loading floors…</p>;

  return (
    <section>
      <h3>Floors</h3>
      <p className="wizard-help">
        Everything on this section auto-saves: rename a floor, pick a
        new image, toggle the default, or drag to reorder — each edit
        goes to the backend immediately. Use the trash icon on a card
        to delete that floor.
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleFloorsReorder}
      >
        <SortableContext
          items={floors.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="wizard-floor-list">
            {floors.map((f, idx) => (
              <SortableFloorCard
                key={f.id}
                floor={f}
                index={idx}
                buf={floorBuffers[f.id] || {}}
                saveState={floorSaveState[f.id]}
                onNameChange={handleFloorNameChange}
                onNameBlur={handleFloorNameBlur}
                onImageChange={handleFloorImageChange}
                onDelete={handleDeleteFloor}
                onSetDefault={() => handleFloorSetDefault(f.id)}
              />
            ))}

            <div className="wizard-floor-card is-new">
              <div className="wizard-floor-drag-spacer" aria-hidden />
              <FloorImageDropZone
                preview={newFloor.filePreview}
                emptyLabel="Drop floor image here or click to browse"
                ariaLabel="Upload image for new floor"
                onFileSelected={handleNewFloorImageChange}
              />
              <div className="wizard-floor-card-fields">
                <label>
                  Name
                  <input
                    type="text"
                    value={newFloor.name}
                    placeholder="e.g. 2F, Basement"
                    onChange={(e) =>
                      setNewFloor((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </label>
                <label className="wizard-inline">
                  <input
                    type="checkbox"
                    checked={newFloor.is_default}
                    onChange={(e) =>
                      setNewFloor((f) => ({
                        ...f,
                        is_default: e.target.checked,
                      }))
                    }
                  />
                  Make default
                </label>
                <div className="wizard-floor-card-order-hint">
                  Fill in a name and drop an image — the new floor is
                  created automatically and slots in at order{' '}
                  <strong>#{floors.length + 1}</strong>.
                </div>
              </div>
              {newFloorSaveState && (
                <div
                  className={`wizard-floor-save-chip is-${newFloorSaveState}`}
                  role="status"
                >
                  {newFloorSaveState === 'saving' && 'Creating…'}
                  {newFloorSaveState === 'saved' && 'Created'}
                  {newFloorSaveState === 'error' && 'Create failed'}
                </div>
              )}
            </div>
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
};

export default FloorManager;
